import { useState, MouseEvent } from 'react';

import { Popover } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';

import {
  List,
  ListItem,
  ListItemText,
  ListSearchInput,
  Scroller,
  Icons,
  Skeleton,
  Tooltip,
  tokens,
  Typography,
  Box,
} from '@cast/design-system';
import { useNavigate } from '@cast/infrastructure-organization';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { useWorkloadsQuery } from '@cast/split-me/hooks/queries/workload-optimization';

import { NavTriggerCaret } from '../NavTriggerCaret';
import {
  getNavTriggerButtonSx,
  navTriggerGap,
  navTriggerLabelSx,
  navTriggerPaddingX,
} from '../navTriggerStyles';

/** Workload id lives on nested Inventory Routes, not the AppLayout match — parse from URL. */
const getWorkloadIdFromPathname = (pathname: string): string | undefined => {
  const match = pathname.match(/\/inventory\/workloads\/([^/]+)/);
  return match?.[1];
};

export const WorkloadSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clusterId } = useParams<{ clusterId: string }>();
  const workloadId = getWorkloadIdFromPathname(location.pathname);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);
  const [searchInputValue, setSearchInputValue] = useState('');
  const menuWidth = anchorEl?.getBoundingClientRect().width;

  const { workloads, isLoading, error, refetch } = useWorkloadsQuery({
    clusterId: clusterId!,
  });

  const currentWorkload = workloads?.find(
    (workload) => workload.id === workloadId
  );
  const filteredWorkloads = (workloads || []).filter((workload) => {
    const query = searchInputValue.toLowerCase();
    if (!query) {
      return true;
    }
    return (
      workload.name?.toLowerCase().includes(query) ||
      workload.id.toLowerCase().includes(query) ||
      workload.namespace?.toLowerCase().includes(query)
    );
  });

  const handleOpen = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const closePopover = () => {
    setAnchorEl(null);
    setSearchInputValue('');
  };

  const handleWorkloadSelect = (selectedWorkloadId: string) => {
    closePopover();
    if (!workloadId || selectedWorkloadId === workloadId) {
      return;
    }
    navigate(
      location.pathname.replace(
        `/inventory/workloads/${workloadId}`,
        `/inventory/workloads/${selectedWorkloadId}`
      )
    );
  };

  const triggerLabel = currentWorkload?.name || workloadId || 'Select workload';

  return (
    <>
      <Box
        component="button"
        type="button"
        alignItems="center"
        px={navTriggerPaddingX}
        onClick={handleOpen}
        disabled={isLoading}
        display="flex"
        gap={navTriggerGap}
        sx={getNavTriggerButtonSx(isPopoverOpen)}
        data-testid="workload-chooser-trigger"
      >
        {isLoading ? (
          <Box
            sx={{
              ...navTriggerLabelSx,
              width: 140,
              maxWidth: 220,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Skeleton variant="rounded" width={140} height={16} />
          </Box>
        ) : (
          <Tooltip title={triggerLabel} wrapIf={triggerLabel.length > 28}>
            <Typography
              variant="P14B"
              noWrap
              sx={{
                ...navTriggerLabelSx,
                color: tokens.theme.fg.action.onGhost,
                maxWidth: 220,
              }}
            >
              {triggerLabel}
            </Typography>
          </Tooltip>
        )}
        <NavTriggerCaret />
      </Box>

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ marginTop: '4px', padding: '4px 0px' }}
        slotProps={{
          paper: {
            sx: { backgroundColor: tokens.theme.bg.structural.surface },
          },
        }}
        disableScrollLock
      >
        <Box
          minWidth={270}
          width={menuWidth}
          data-testid="workload-chooser-menu"
        >
          <ListSearchInput
            autoFocus
            autoComplete="off"
            size="small"
            placeholder="Enter workload name"
            listItemProps={{
              sx: { '&.Mui-focusVisible': { background: 'none' } },
            }}
            value={searchInputValue}
            onChange={(_: unknown, v: string) => setSearchInputValue(v)}
            showClearButton
            clearButtonProps={{
              onClick: () => setSearchInputValue(''),
            }}
            disabled={isLoading || !!error || !workloads?.length}
          />
          <List>
            {isLoading ? (
              <Box px={12} py={8}>
                <Skeleton variant="rounded" height={32} />
                <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
              </Box>
            ) : error ? (
              <Box px={12} py={16}>
                <FailedToLoad
                  title="Failed to load workloads"
                  refresh={refetch}
                  compact
                />
              </Box>
            ) : !filteredWorkloads.length ? (
              <ListItem
                startAdornment={<Icons.Nut size={14} />}
                startAdornmentSx={{
                  alignSelf: 'flex-start',
                  marginTop: '2px',
                }}
                multiline
              >
                <ListItemText
                  primary="No workloads found"
                  secondary="Adjust your search criteria"
                />
              </ListItem>
            ) : (
              <Scroller
                sx={{ gap: '2px', overflow: 'auto', maxHeight: '225px' }}
              >
                {filteredWorkloads.map((workload) => (
                  <ListItem
                    key={workload.id}
                    onClick={() => handleWorkloadSelect(workload.id)}
                    selected={workload.id === workloadId}
                    multiline
                  >
                    <ListItemText
                      primary={workload.name || workload.id}
                      primaryTypographyProps={{ variant: 'P12M', noWrap: true }}
                      secondary={workload.namespace}
                    />
                  </ListItem>
                ))}
              </Scroller>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};
